import { fetchWithRefresh } from "../../api/FetchWithRefresh";
import { useState, useCallback } from "react";
import type { paths } from "../../api/generated/api-types";

// Единая форма тела ошибки бэкенда: обработчики читают из него текст сообщения.
interface ErrorBody {
  // Отсутствует, когда бэкенд ответил ошибкой без разобранного тела: сообщение
  // тогда собирается из кода ответа.
  error?: string;
}

export type GenerationControlResponse =
  paths["/generation/queue/control"]["post"]["responses"]["200"]["content"]["application/json"];

export type PublicationPauseResponse =
  paths["/publications/queue/pause"]["post"]["responses"]["200"]["content"]["application/json"];

export type PublicationResumeResponse =
  paths["/publications/queue/resume"]["post"]["responses"]["200"]["content"]["application/json"];

type PublicationControlResponse = PublicationPauseResponse | PublicationResumeResponse;

type QueueAction = "pause" | "resume";

export type QueueControlResult = GenerationControlResponse | PublicationControlResponse;

export type ActionState<TResult extends QueueControlResult = QueueControlResult> =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "success"; result: TResult };

export interface UseDashboardActionsResult {
  generationActionState: ActionState<GenerationControlResponse>;
  publicationActionState: ActionState<PublicationControlResponse>;
  pauseGeneration: () => void;
  resumeGeneration: () => void;
  pausePublication: () => void;
  resumePublication: () => void;
}

export function useDashboardActions(): UseDashboardActionsResult {
  const [generationActionState, setGenerationActionState] = useState<ActionState<GenerationControlResponse>>({ kind: "idle" });
  const [publicationActionState, setPublicationActionState] = useState<ActionState<PublicationControlResponse>>({ kind: "idle" });

  const controlGeneration = useCallback((action: QueueAction) => {
    setGenerationActionState({ kind: "loading" });

    fetchWithRefresh("/api/generation/queue/control", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action }),
    })
      .then(async (response) => {
        if (response.ok) {
          const data = (await response.json()) as GenerationControlResponse;
          setGenerationActionState({ kind: "success", result: data });
        } else {
          const errorBody = (await response.json()) as ErrorBody;
          setGenerationActionState({
            kind: "error",
            message: errorBody.error ?? "Ошибка управления очередью генерации",
          });
        }
      })
      .catch(() => {
        setGenerationActionState({ kind: "error", message: "Ошибка сети" });
      });
  }, []);

  const controlPublication = useCallback((action: QueueAction) => {
    setPublicationActionState({ kind: "loading" });

    fetchWithRefresh(`/api/publications/queue/${action}`, {
      method: "POST",
    })
      .then(async (response) => {
        if (response.ok) {
          const data = (await response.json()) as PublicationControlResponse;
          setPublicationActionState({ kind: "success", result: data });
        } else {
          const errorBody = (await response.json()) as ErrorBody;
          setPublicationActionState({
            kind: "error",
            message: errorBody.error ?? "Ошибка управления очередью публикации",
          });
        }
      })
      .catch(() => {
        setPublicationActionState({ kind: "error", message: "Ошибка сети" });
      });
  }, []);

  const pauseGeneration = useCallback(() => controlGeneration("pause"), [controlGeneration]);
  const resumeGeneration = useCallback(() => controlGeneration("resume"), [controlGeneration]);
  const pausePublication = useCallback(() => controlPublication("pause"), [controlPublication]);
  const resumePublication = useCallback(() => controlPublication("resume"), [controlPublication]);

  return {
    generationActionState,
    publicationActionState,
    pauseGeneration,
    resumeGeneration,
    pausePublication,
    resumePublication,
  };
}
